<template lang="pug">
  #member-center-admin-create-notice
    BNotification(:active.sync="error" type="is-danger" :duration="3000")
      | {{ errorMessage }}
    BNotification.notice(
      type="is-lightgrey"
      aria-close-label="Close notification"
      v-if="previewNotice"
      @close="previewNotice = false"
    )
      WtDisplayRichTextHTML(:html="content")
    span(v-show="!previewNotice")
      BField(
        label=""
        label-for="notice"
      )
        WtWysiwyg#notice-wysiwyg(selector="#notice-wysiwyg" :content.sync="content")
      .columns
        BField.column.is-3
          WtLabel(
            slot="label"
            text="Start Date"
            :tooltip-text="startDateTooltip"
            label-for="duration"
          )
          BDatepicker(
            placeholder="Click to select..."
            icon="calendar-today"
            editable
            v-model="begins"
            :date-formatter="(date) => this.date(date, 'DATE_FORMAT_SHORT_MONTH').formatted"
            position="is-top-right"
            :min-date="startOfToday().dateObject"
          )
        BField.column.is-3
          WtLabel(
            slot="label"
            text="End Date"
            :tooltip-text="endDateTooltip"
            label-for="duration"
          )
          BDatepicker(
            placeholder="Click to select..."
            icon="calendar-today"
            editable
            v-model="expires"
            :date-formatter="(date) => this.date(date, 'DATE_FORMAT_SHORT_MONTH').formatted"
            position="is-top-right"
            :min-date="begins"
          )

    .level
    .level.is-mobile
      .level-left
      .level-right
        .level-item
          WtButton(
            color="primary"
            outlined
            size="medium"
            semibold
            @click.native="previewNotice = !previewNotice"
            :disabled="!content"
          ) PREVIEW NOTICE
        .level-item
          WtButton(
            color="primary"
            semibold
            size="medium"
            :loading="noticeWorking"
            @click.native="submit"
            :disabled="!content"
          ) {{ submitButtonText }}
</template>

<script>
import { mapActions } from "vuex";
import WtWysiwyg from "@@/WtWysiwyg/WtWysiwyg";
import WtTitle from "@@/WtTitle/WtTitle";
import WtButton from "@@/WtButton/WtButton";
import WtTooltip from "@@/WtTooltip/WtTooltip";
import WtLabel from "@@/WtLabel/WtLabel";
import WtTile from "@@/WtTile/WtTile";
import WtDisplayRichTextHTML from "@@/WtDisplayRichTextHTML/WtDisplayRichTextHTML";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";

export default {
  name: "MemberCenterAdminCreateNotice",

  components: {
    WtWysiwyg,
    WtTooltip,
    WtLabel,
    WtTitle,
    WtButton,
    WtTile,
    WtDisplayRichTextHTML
  },

  mixins: [dateUtilityMixin],

  props: {
    notice: {
      type: Object,
      default: () => {}
    },
    editNoticeMode: {
      type: Boolean,
      default: false
    },
    goBackToNoticeList: {
      type: Function,
      required: true
    }
  },

  data() {
    return {
      error: false,
      errorMessage: null,
      content: this._.get(this.notice, "text", null),
      duration: 30,
      expires: this.dateAfter(30).dateObject,
      begins: this.startOfToday().dateObject,
      previewNotice: false,
      noticeExists: false,
      noticeWorking: false
    };
  },

  computed: {
    startDateTooltip() {
      return `Notice will appear on members dashboards starting with this date.`;
    },

    endDateTooltip() {
      return `Notice will remain on members dashboards until this date.`;
    },

    submitButtonText() {
      return this.editNoticeMode ? "UPDATE NOTICE" : "POST NOTICE";
    },

    resetButtonText() {
      return this.noticeExists ? "RESET" : "CLEAR";
    }
  },

  watch: {
    begins() {
      if (
        this.moment(this.expires).diff(this.moment(this.begins), "days") < 0
      ) {
        this.expires = this.begins;
      }
    },
    notice() {
      if (this._.has(this.notice, "text")) {
        this.noticeExists = true;
        this.content = this.notice.text;
        this.expires = this.moment(this.notice.expires)._d;
        this.begins = this.moment(this.notice.begins)._d;
      }
    }
  },

  methods: {
    ...mapActions(["snackBar"]),
    ...mapActions("notices", [
      "createAdminMemberNotice",
      "updateAdminMemberNotice",
      "removeAdminMemberNotice",
      "setProgramAndMemberNotices",
      "setUpdatedProgramAndMemberNotices"
    ]),

    reset() {
      return this.noticeExists ? this.resetNotice() : this.clearNotice();
    },

    clearNotice() {
      this.content = ""; // needs to be string for WtWysiwyg to be happy
      this.duration = 30;
      this.expires = this.dateAfter(30).dateObject;
      this.begins = this.startOfToday().dateObject;
      this.noticeExists = false;
      this.previewNotice = false;
    },

    resetNotice() {
      this.content = this.notice.text;
      this.expires = this.date(this.notice.expires).dateObject;
    },

    submit() {
      this.noticeWorking = true;

      if (this.editNoticeMode) {
        return this.updateNotice();
      }
      return this.createNotice();
    },

    async updateNotice() {
      const response = await this.updateAdminMemberNotice({
        noticeId: this.notice.id,
        message: this.content,
        begins: this.moment(this.begins).format("YYYY-MM-DD"),
        expires: this.moment(this.expires).format("YYYY-MM-DD")
      });

      if (response.error) {
        this.error = true;
        this.errorMessage = response.message;
      } else {
        this.snackBar({
          message: "Notice has been updated."
        });
        this.setUpdatedProgramAndMemberNotices(response.data.data).then(() => {
          this.clearNotice();
          this.goBackToNoticeList();
        });
      }

      this.noticeWorking = false;
    },

    async createNotice() {
      const response = await this.createAdminMemberNotice({
        message: this.content,
        duration: this.duration,
        begins: this.moment(this.begins).format("YYYY-MM-DD"),
        expires: this.moment(this.expires).format("YYYY-MM-DD")
      });

      if (response.error) {
        this.error = true;
        this.errorMessage = response.message;
      } else {
        this.snackBar({
          message: "Notice has been created."
        });

        this.noticeExists = true;
        this.clearNotice();

        this.setProgramAndMemberNotices(response.data.data);
      }

      this.noticeWorking = false;
    }
  }
};
</script>

<style lang="sass">
#member-center-admin-create-notice
  .notice
    min-height: 200px
</style>
